import type { JavaScriptFunction } from '@unifyapps/defs/types/javascriptFunction';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useCallback } from 'react';
import { executeFunction } from '../../../../components/PageFunctionController/PageFunctionExecutor/executeFunction';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import {
  useAppDependencyGraphProvider,
  useGetDependencyGraphProvider,
} from '../../../../context/DependencyGraphProvider';
import type { DependencyGraphContextType } from '../../../../context/DependencyGraphProvider/types';
import InterfacePageHelper from '../../../../helper/InterfacePage';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../../../stores/app/GlobalAppStateStore';
import {
  type GlobalStateStoreType,
  useGetGlobalStateStoreStateByInstanceId,
} from '../../../../stores/GlobalStateStore';
import {
  useGetActivePageByInstanceId,
  useInterfaceAppStore,
  useInterfaceStore,
} from '../../../../stores/InterfaceStore';
import { ComputeContextKeys } from '../../../computeContext/const';
import { useGetShouldEvaluateResult } from '../../../computeContext/useGetShouldEvaluateResult';
import { useReportActionError } from '../../../useReportActionError';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';

type PageFunctionDependencies = {
  getPageFunctionDetails: (
    functionId: string,
    instanceId?: string,
  ) => JavaScriptFunction | undefined;
  getPageFunctionState: (
    functionId: string,
    instanceId?: string,
  ) => GlobalStateStoreType['pageFunctions'][string] | undefined;
  setPageFunctionState: (
    functionId: string,
    state: GlobalStateStoreType['pageFunctions'][string],
    instanceId?: string,
  ) => void;
  getDependencyGraphProvider: (instanceId?: string) => DependencyGraphContextType;
};

const useTriggerFunctionBase = (deps: PageFunctionDependencies) => {
  const shouldEvaluateResult = useGetShouldEvaluateResult();
  const reportActionError = useReportActionError();

  const onTriggerFunction: ActionHandlerType = useEventCallback(
    async ({ action, actionContext, instanceId, blockId }: OnActionArgs) => {
      const functionId = action.payload?.functionId as string | undefined;

      if (!functionId) {
        console.error('Function ID is required to trigger a function');
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      const pageFunction = deps.getPageFunctionDetails(functionId, instanceId);

      if (!pageFunction) {
        // function not found in this level, propagate up to check app level
        return Promise.resolve(SHOULD_PROPAGATE_UP);
      }

      const result = await Promise.resolve(
        executeFunction({
          context: actionContext,
          functionBody: pageFunction.functionBody,
          shouldEvaluateResult,
          functionId,
        }),
      );

      if (result.error) {
        // the function threw, so the error row points to the function itself (matching where its
        // logs attribute), not the block that triggered it
        reportActionError({
          source: 'pageFunction',
          error: result.error,
          instanceId,
          entity: { type: 'pageFunction', id: functionId },
          expression: pageFunction.functionBody,
        });
      }

      deps.setPageFunctionState(
        functionId,
        {
          ...pageFunction,
          value: result.error ? undefined : result.value,
          error: result.error,
          isEvaluated: true,
        },
        instanceId,
      );
      const {
        actions: { notifyDependencyChange },
      } = deps.getDependencyGraphProvider(instanceId);
      notifyDependencyChange({ id: functionId });
      notifyDependencyChange({ id: ComputeContextKeys.PAGE_FUNCTIONS });

      // re-throw so EventHandler routes to onErrorActions instead of onSuccessActions
      if (result.error) {
        throw new Error(result.error.message);
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
  );

  return { onTriggerFunction };
};

export function useTriggerFunction() {
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const getActivePageByInstanceId = useGetActivePageByInstanceId();
  const pageFunctions = useInterfaceStore().hooks.page.usePageFunctions();
  const { getDependencyGraphProvider } = useGetDependencyGraphProvider();

  const getPageFunctionDetails = useCallback(
    (functionId: string, instanceId?: string) => {
      if (instanceId) {
        const page = getActivePageByInstanceId(instanceId);
        if (page) {
          return InterfacePageHelper.getPageFunctionById({
            interfacePage: page,
            id: functionId,
          });
        }
      }
      return pageFunctions?.[functionId];
    },
    [getActivePageByInstanceId, pageFunctions],
  );

  const getPageFunctionState = useCallback(
    (functionId: string, instanceId?: string) => {
      return getGlobalStateStoreState(instanceId).pageFunctions[functionId];
    },
    [getGlobalStateStoreState],
  );

  const setPageFunctionStateInContext = useCallback(
    (
      functionId: string,
      state: GlobalStateStoreType['pageFunctions'][string],
      instanceId?: string,
    ) => {
      getGlobalStateStoreState(instanceId).actions.setPageFunctionState(functionId, state);
    },
    [getGlobalStateStoreState],
  );

  return useTriggerFunctionBase({
    getPageFunctionDetails,
    getPageFunctionState,
    setPageFunctionState: setPageFunctionStateInContext,
    getDependencyGraphProvider,
  });
}

export const useTriggerGlobalPageFunction = () => {
  const globalAppStateStoreActions = useGlobalAppStateStore().hooks.useActions();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const pageFunctions = useInterfaceAppStore().hooks.page.usePageFunctions();
  const appDependencyGraphProvider = useAppDependencyGraphProvider();

  const getPageFunctionDetails = useCallback(
    (functionId: string, _instanceId?: string) => {
      return pageFunctions?.[functionId];
    },
    [pageFunctions],
  );

  const getPageFunctionState = useCallback(
    (functionId: string, _instanceId?: string) => {
      return getGlobalAppStateStoreState().pageFunctions[functionId];
    },
    [getGlobalAppStateStoreState],
  );

  const setPageFunctionState = useCallback(
    (
      functionId: string,
      state: GlobalStateStoreType['pageFunctions'][string],
      _instanceId?: string,
    ) => {
      globalAppStateStoreActions.setPageFunctionState(functionId, state);
    },
    [globalAppStateStoreActions],
  );

  const getDependencyGraphProvider = useCallback(() => {
    return appDependencyGraphProvider;
  }, [appDependencyGraphProvider]);

  return useTriggerFunctionBase({
    getPageFunctionDetails,
    getPageFunctionState,
    setPageFunctionState,
    getDependencyGraphProvider,
  });
};

export default useTriggerFunction;
